import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { GridColumns } from "@mui/x-data-grid";
import { Grid, Tab, Tabs } from "@mui/material";
import TabPanel from "../../../reusable-components/TabPanel/TabPanel";
import TnACounter from "./TnACounter";
import { TnACounterDTO } from "../../../models/tna";
import { TnAMobileHoursBalanceDTO } from "../../../models/tna/tna-mobile-hours/tna-mobile-hours-balance-dto";
import TnAMobileHours from "./TnAMobileHours";

interface Props {
  counters: TnACounterDTO[];
  smallSize?: boolean;
  mobileHours?: TnAMobileHoursBalanceDTO[];
  showMobileHours?: boolean;
}

const TnACountersAndMobileHoursTable = ({ counters, mobileHours, smallSize = false, showMobileHours }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_calendar";

  const [value, setValue] = useState<number>(0);

  const columns: GridColumns = [
    {
      field: "year",
      headerName: "",
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
      align: "center",
    },
    {
      field: "counterType",
      headerName: "",
      valueGetter: ({ row }) => row.counterType?.name,
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: "fromPreviousYear",
      headerName: t(`${tRP}.fields.from_previous_year`),
      valueGetter: ({ row }) => `${Number(row.fromPrevYear).toFixed(2)} ${t(`${tRP}.days`)}`,
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: "alreadyGeneratedThisYear",
      headerName: t(`${tRP}.fields.already_generated_this_year`),
      valueGetter: ({ row }) => `${Number(row.generatedThisYear).toFixed(2)} ${t(`${tRP}.days`)}`,
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: "corrections",
      headerName: t(`${tRP}.fields.corrections`),
      valueGetter: ({ row }) => `${row.correctionsThisYear} ${t(`${tRP}.days`)}`,
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: "alreadyUsedOrPlanned",
      headerName: t(`${tRP}.fields.already_used_or_planned`),
      valueGetter: ({ row }) => `${Number(row.usedOrPlanned).toFixed(2)} ${t(`${tRP}.days`)}`,
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: "totalBalance",
      headerName: t(`${tRP}.fields.total_balance`),
      valueGetter: ({ row }) => `${Number(row.balance).toFixed(2)} ${t(`${tRP}.days`)}`,
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
    },
  ];

  const panels = [
    ...counters.map((counter: TnACounterDTO) => ({
      title: counter.counterType?.name,
      component: (
        <TnACounter
          counter={counter}
          smallSize={smallSize}
        />
      ),
    })),
    ...(showMobileHours && Array.isArray(mobileHours)
      ? mobileHours?.map((mobileHour, index) => ({
          title: t(`${tRP}.fields.mobile_hours`),
          component: (
            <TnAMobileHours
              key={index}
              mobileHours={mobileHour}
              smallSize={smallSize}
            />
          ),
        }))
      : []),
  ];

  const handleChangeValue = useCallback((value: number) => {
    setValue(value);
  }, []);

  return (
    <Grid>
      <Tabs
        value={value}
        onChange={(_, value) => handleChangeValue(value)}
        TabIndicatorProps={{
          style: {
            width: "0px",
          },
        }}
        style={{
          minHeight: "35px",
        }}
      >
        {panels.map((panel, idx) => (
          <Tab
            label={panel.title}
            key={idx}
            iconPosition={"start"}
            disableRipple={true}
            style={{
              padding: "5px 0px",
              marginRight: "10px",
              textTransform: "none",
              minHeight: "unset",
              justifyContent: "flex-start",
            }}
          />
        ))}
      </Tabs>

      {panels.map((panel, idx) => (
        <TabPanel
          key={idx}
          value={value}
          index={idx}
        >
          {panel.component}
        </TabPanel>
      ))}
    </Grid>
  );
};

export default TnACountersAndMobileHoursTable;
